import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Actionable, AspectRatio, Avatar, Carousel, Image, Stack, Text, Title, useViewport } from "@bookingcom/bui-react";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { getFlyAnywhereCountries } from "../../../store/flyAnywhere/selectors.js";
import { getSearch } from "../../../store/search/selectors.js";
import styles from "./FlyAnywhereCountriesCarousel.module.css";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { mcn } from "../../../utils/mergeClassnames.js";
import flights_web_cat_fly_anywhere_map_view_www from "../../../utils/experiments/cat/flights_web_cat_fly_anywhere_map_view_www.js";
import flights_web_cat_fly_anywhere_map_view_mdot from "../../../utils/experiments/cat/flights_web_cat_fly_anywhere_map_view_mdot.js";
import { trackWithDefaultStage, stage, customGoal } from "@flights/et-universal";
import useFlyAnywhereC360Events from "../../../hooks/c360/useFlyAnywhereC360Events.js";
import useUserAgent from "../../../hooks/useUserAgent.js";
import { useIsMapViewExp } from "./hooks/useIsMapViewExp.js";
const FlyAnywhereSpecialCard = ({ createUrlWithApiPrefix, isSmall }) => {
    const i18n = useI18n();
    const isMapViewExp = useIsMapViewExp();
    const { isMobile } = useUserAgent();
    const mapViewExp = isMobile ? flights_web_cat_fly_anywhere_map_view_mdot : flights_web_cat_fly_anywhere_map_view_www;
    const href = createUrlWithApiPrefix(isMapViewExp ? "/fly-anywhere?flyAnywhereView=map" : "/fly-anywhere");
    return (React.createElement(Actionable, { className: styles.card, href: href, onClick: () => {
            customGoal("flights_web_cat_fly_anywhere_illustration", 1);
            if (isMapViewExp) {
                mapViewExp.goals.discoverCarousel();
            }
        } },
        React.createElement(AspectRatio, { ratio: isSmall ? "5:4" : "4:5" },
            React.createElement(Stack, { className: styles.specialCard },
                React.createElement(Image, { className: styles.specialCardImage, asset: {
                        setName: "illustrations-traveller",
                        assetName: "TripsGlobe"
                    }, contentMode: "fit" }),
                React.createElement(Text, { variant: "headline_3", color: "white" }, i18n.trans(t("flights_explore_by_country_card_anywhere_title"))),
                React.createElement(Text, { variant: "body_1", color: "white", className: styles.specialCardSubtitle }, i18n.trans(t("flights_explore_by_country_card_anywhere_subtitle")))))));
};
const FlyAnywhereSpecialCardV2 = ({ createUrlWithApiPrefix, isSmall }) => {
    const i18n = useI18n();
    const isMapViewExp = useIsMapViewExp();
    const { isMobile } = useUserAgent();
    const mapViewExp = isMobile ? flights_web_cat_fly_anywhere_map_view_mdot : flights_web_cat_fly_anywhere_map_view_www;
    const href = createUrlWithApiPrefix(isMapViewExp ? "/fly-anywhere?flyAnywhereView=map" : "/fly-anywhere");
    return (React.createElement(Actionable, { className: styles.card, href: href, onClick: () => {
            customGoal("flights_web_cat_fly_anywhere_illustration", 1);
            if (isMapViewExp) {
                mapViewExp.goals.discoverCarousel();
            }
        } },
        React.createElement(AspectRatio, { ratio: isSmall ? "5:4" : "4:5" },
            React.createElement(Stack, { className: mcn(styles.specialCard, styles.specialCardV2) },
                React.createElement(Stack.Item, null,
                    React.createElement(Text, { variant: "headline_3", color: "white", tagName: "strong" }, i18n.trans(t("flights_explore_by_country_card_anywhere_title"))),
                    React.createElement(Text, { variant: "body_1", color: "white", className: styles.specialCardSubtitle }, i18n.trans(t("flights_explore_by_country_card_anywhere_subtitle")))),
                React.createElement(Image, { className: mcn(styles.specialCardImage, styles.specialCardImageV2), asset: {
                        setName: "illustrations-traveller",
                        assetName: "FlightsExploreAnywhereIndexPage"
                    }, contentMode: "fit" })))));
};
const CountryCard = ({ country, getCardUrl, isSmall, onCardClick }) => {
    const { code, imgSlug, translatedCountryName } = country;
    return (React.createElement(Actionable, { className: styles.card, href: getCardUrl(code), onClick: () => {
            onCardClick(code);
        } },
        React.createElement(AspectRatio, { ratio: isSmall ? "5:4" : "4:5" },
            React.createElement("div", { className: styles.imageWrapper },
                React.createElement(Image, { src: imgSlug, borderRadius: 200, className: styles.image }),
                React.createElement("div", { className: styles.imageOverlay }),
                React.createElement(Stack, { className: styles.textContent, direction: "row" },
                    React.createElement(Avatar, { countryCode: code, size: "small", className: styles.flag }),
                    React.createElement(Text, { variant: "headline_3", color: "white", className: styles.countryName }, translatedCountryName))))));
};
const FlyAnywhereCountriesCarousel = () => {
    const i18n = useI18n();
    const countries = useSelector(getFlyAnywhereCountries);
    const searchState = useSelector(getSearch);
    const { isSmall, isLarge } = useViewport();
    const { createUrlWithApiPrefix } = useCreateUrl();
    const [carouselKey, setCarouselKey] = useState(0);
    const trackFlyAnywhereC360 = useFlyAnywhereC360Events();
    const isMapViewExp = useIsMapViewExp();
    const { isMobile } = useUserAgent();
    const isFlyAnywhereIllustrationExp = trackWithDefaultStage("flights_web_cat_fly_anywhere_illustration", 1) === 1;
    stage("flights_web_cat_fly_anywhere_illustration", isMobile ? 3 : 2);
    const itemsToRender = useMemo(() => {
        const items = countries ? [...countries] : [];
        if (items.length > 0) {
            let specialCardIndex = 3;
            if (isSmall) {
                specialCardIndex = 1;
            }
            if (specialCardIndex < items.length) {
                items.splice(specialCardIndex, 0, { isSpecialCard: true });
            }
            else {
                items.push({ isSpecialCard: true });
            }
        }
        else {
            items.push({ isSpecialCard: true });
        }
        return items;
    }, [countries, isSmall]);
    useEffect(() => {
        setCarouselKey((prevKey) => prevKey + 1);
    }, [isSmall, countries]);
    if (!countries?.length) {
        return null;
    }
    const getCardUrl = (countryCode) => {
        const params = new URLSearchParams();
        params.set("toCountryCode", countryCode);
        if (isMapViewExp) {
            params.set("flyAnywhereView", "map");
        }
        return createUrlWithApiPrefix(`/fly-anywhere?${params.toString()}`);
    };
    const handleCardClick = (countryCode) => {
        const segment = searchState.searchSegments?.[0];
        trackFlyAnywhereC360("click_FA_EC_index", {
            context: "index",
            inbound_destination: segment?.from?.[0]?.name || "",
            outbound_destination: countryCode,
            inbound_dates: segment?.departureDate || "",
            outbound_dates: segment?.returnDate || "",
            journey_type: searchState.type || "ROUNDTRIP",
            cabin_class: searchState.cabinClass || "ECONOMY",
            direct: searchState.isDirect,
            number_of_travellers: {
                adults: searchState.adults || 1,
                children: searchState.children?.length || 0
            }
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Title, { title: i18n.trans(t("flights_explore_by_country_header")), subtitle: i18n.trans(t("flights_explore_by_country_subheader")), variant: "headline_2", titleTagName: "h2", titleAttributes: { id: "fly-anywhere-countries-title" } }),
        React.createElement(Carousel, { key: carouselKey, size: isSmall ? "large" : "medium", nextButtonAriaLabel: i18n.trans(t("a11y_flights_route_carousel_next_button_aria_label")), previousButtonAriaLabel: i18n.trans(t("a11y_flights_route_carousel_previous_button_aria_label")), className: mcn(styles.carousel, !isLarge && styles.carouselMd), attributes: {
                role: "group",
                "aria-labelledby": "fly-anywhere-countries-title"
            } }, itemsToRender.map((item) => {
            if ("isSpecialCard" in item) {
                return isFlyAnywhereIllustrationExp ? (React.createElement(FlyAnywhereSpecialCardV2, { key: "special-card", createUrlWithApiPrefix: createUrlWithApiPrefix, isSmall: isSmall })) : (React.createElement(FlyAnywhereSpecialCard, { key: "special-card", createUrlWithApiPrefix: createUrlWithApiPrefix, isSmall: isSmall }));
            }
            return (React.createElement(CountryCard, { key: item.code, country: item, getCardUrl: getCardUrl, isSmall: isSmall, onCardClick: handleCardClick }));
        }))));
};
export default FlyAnywhereCountriesCarousel;
